import Logger from '../utils/logger';

// Load tenant mappings (domain -> slug)
let tenantMappings = {};
try {
  // Try to load tenant mappings from config
  const mappingsModule = require('../../config/tenant-mappings.json');
  tenantMappings = mappingsModule['tenant-mappings'] || {};
} catch (error) {
  // If mappings file doesn't exist, use empty object (will fallback to hostname extraction)
  Logger.warn('Tenant mappings file not found, using hostname extraction only');
}

/**
 * Tenant Resolution Service
 * Handles tenant identification from hostname for frontend
 * Backend middleware handles the actual tenant resolution
 */
class TenantResolver {
  constructor() {
    this.currentTenantSlug = null;
    this.initialized = false;
    this.tenantMappings = tenantMappings;
    this.setFromUserData = false; // Track if tenant was set from userData
  }

  /**
   * Initialize tenant resolver based on current hostname
   * Only extracts from hostname if tenantId is not already set from userData
   */
  initialize() {
    try {
      // First check if tenantId exists in userData - if so, use that instead of hostname
      const tenantIdFromUser = this.getTenantIdFromUserData();
      if (tenantIdFromUser) {
        Logger.info('Initializing tenant resolver with userData tenantId', { tenantId: tenantIdFromUser });
        this.currentTenantSlug = tenantIdFromUser;
        this.setFromUserData = true;
        this.initialized = true;
        Logger.info('Tenant resolver initialized from userData', { tenantSlug: this.currentTenantSlug });
        return this.currentTenantSlug;
      }

      // If tenant was already set from userData, don't overwrite it
      if (this.setFromUserData && this.currentTenantSlug) {
        Logger.info('Tenant already set from userData, skipping hostname extraction', { tenantSlug: this.currentTenantSlug });
        this.initialized = true;
        return this.currentTenantSlug;
      }

      // Fallback to hostname extraction only if no userData tenantId exists
      const hostname = window.location.hostname;
      const tenantSlug = this.extractTenantFromHostname(hostname);
      
      Logger.info('Initializing tenant resolver from hostname', { hostname, tenantSlug });
      
      this.currentTenantSlug = tenantSlug || this.getDefaultTenant();
      this.initialized = true;
      
      Logger.info('Tenant resolver initialized from hostname', { tenantSlug: this.currentTenantSlug });
      return this.currentTenantSlug;
    } catch (error) {
      Logger.error('Failed to initialize tenant resolver:', error);
      this.initialized = true;
      this.currentTenantSlug = this.getDefaultTenant();
      return this.currentTenantSlug;
    }
  }

  /**
   * Extract tenant slug from hostname
   * Supports patterns:
   * - *.bitsmith.in (production)
   * - *.bitsmith.local (development)
   * - Custom domain mappings via tenant-mappings.json
   */
  extractTenantFromHostname(hostname) {
    // Check tenant resolution mode from environment
    const resolutionMode = process.env.REACT_APP_TENANT_RESOLUTION_MODE || 'hostname';
    
    if (resolutionMode === 'param') {
      // Extract from URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('tenant') || this.getDefaultTenant();
    }
    
    if (resolutionMode === 'fixed') {
      // Use fixed tenant from environment
      return this.getDefaultTenant();
    }
    
    // First, check if there's a mapping for this exact domain
    if (this.tenantMappings && this.tenantMappings[hostname]) {
      Logger.info('Using tenant mapping', { hostname, slug: this.tenantMappings[hostname] });
      return this.tenantMappings[hostname];
    }
    
    // Production pattern: tenant.bitsmith.in
    if (hostname.endsWith('.bitsmith.in')) {
      const parts = hostname.split('.');
      if (parts.length >= 3) {
        return parts[0]; // Extract subdomain as-is
      }
    }
    
    // Development pattern: tenant.bitsmith.local (keep full subdomain including -development)
    if (hostname.endsWith('.bitsmith.local')) {
      const parts = hostname.split('.');
      if (parts.length >= 3) {
        return parts[0]; // Extract full subdomain (e.g., 'ffss-development')
      }
    }
    
    // Localhost patterns for development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // Check for tenant in URL params or default
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('tenant') || this.getDefaultTenant();
    }
    
    // Default fallback
    return this.getDefaultTenant();
  }
  
  /**
   * Reload tenant mappings from config file (useful for development)
   */
  reloadMappings() {
    try {
      const mappingsModule = require('../../config/tenant-mappings.json');
      this.tenantMappings = mappingsModule['tenant-mappings'] || {};
      Logger.info('Tenant mappings reloaded', { mappings: this.tenantMappings });
    } catch (error) {
      Logger.warn('Failed to reload tenant mappings', error);
    }
  }

  /**
   * Get default tenant based on environment
   */
  getDefaultTenant() {
    return process.env.REACT_APP_DEFAULT_TENANT || 'ffss-development';
  }

  /**
   * Get current tenant slug (for API calls)
   */
  getCurrentTenantSlug() {
    return this.currentTenantSlug || this.getDefaultTenant();
  }

  /**
   * Set tenant ID from user data (called after login)
   * This takes precedence over hostname-based resolution
   */
  setTenantFromUserData(tenantId) {
    if (tenantId) {
      Logger.info('Setting tenant from user data', { tenantId });
      this.currentTenantSlug = tenantId;
      this.setFromUserData = true;
      this.initialized = true;
    }
  }

  /**
   * Get tenant ID from userData if available, otherwise use resolver
   */
  getTenantIdFromUserData() {
    try {
      const userDataStr = localStorage.getItem("userData");
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        if (userData?.tenantId) {
          return userData.tenantId;
        }
      }
    } catch (error) {
      Logger.warn('Failed to parse userData for tenantId', error);
    }
    return null;
  }

  /**
   * Get current tenant ID for API calls (same as slug - backend will resolve)
   * Priority: userData.tenantId > currentTenantSlug > default
   */
  getCurrentTenantId() {
    // First check if tenantId is in userData (from login)
    const tenantIdFromUser = this.getTenantIdFromUserData();
    if (tenantIdFromUser) {
      return tenantIdFromUser;
    }
    // Fallback to resolved tenant slug
    return this.getCurrentTenantSlug();
  }

  /**
   * Check if tenant resolver is initialized
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Clear cache (for testing)
   */
  clearCache() {
    this.currentTenantSlug = null;
    this.initialized = false;
    this.setFromUserData = false;
  }
}

// Create singleton instance
const tenantResolver = new TenantResolver();

export default tenantResolver;

// Named exports for convenience
export const initializeTenantResolver = () => tenantResolver.initialize();
export const getCurrentTenantId = () => tenantResolver.getCurrentTenantId();
export const getCurrentTenantSlug = () => tenantResolver.getCurrentTenantSlug();